import { Injectable } from '@angular/core';
import { StringUtil } from '../utils/string.util';

export interface NameSuggestion {
  name: string;
  score: number;
  matchType: 'exact' | 'starts' | 'contains' | 'similar';
  category: 'similar' | 'variation' | 'generated';
  reason?: string;
}

export interface NameCheckResult {
  available: boolean;
  suggestions: NameSuggestion[];
  message: string;
}

@Injectable({
  providedIn: 'root',
})
export class NameSuggestionService {
  private readonly takenNames = [
    'ZIMTECH SOLUTIONS',
    'AFRITECH INNOVATIONS',
    'VICTORIA FALLS TRADING',
    'HARARE ENTERPRISES',
    'BULAWAYO INDUSTRIES',
    'ZIMBABWE HOLDINGS',
    'TECH AFRICA',
    'FINTECH SOLUTIONS',
    'CAPITAL INVESTMENTS',
    'DELTA CORPORATION',
    'ROYAL ENTERPRISES',
    'PREMIER HOLDINGS',
    'UNITED TECHNOLOGIES',
    'GLOBAL INNOVATIONS',
    'FIRST CAPITAL',
    'ELITE SOLUTIONS',
    'CROWN INVESTMENTS',
    'SUMMIT GROUP',
    'NEXUS AFRICA',
    'QUANTUM VENTURES',
  ];

  private readonly prefixes = [
    'New', 'Premier', 'United', 'First', 'Royal', 'Crown', 'Elite', 'Prime', 'Global', 'African',
  ];

  private readonly suffixes = [
    'Holdings', 'Group', 'Enterprises', 'Solutions', 'International', 'Global', 'Africa', 'Zimbabwe', 'Innovations', 'Technologies', 'Partners', 'Ventures',
  ];

  private readonly ignoredWords = [
    'LIMITED', 'LTD', 'HOLDINGS', 'GROUP', 'ENTERPRISES', 'SOLUTIONS', 'TECHNOLOGIES', 'INTERNATIONAL',
  ];

  private readonly SIMILARITY_BLOCK = 0.9;
  private readonly SIMILARITY_WARN = 0.8;

  private readonly normalizedTakenNames = this.takenNames.map(name => StringUtil.normalize(name));


  checkNameAvailability(companyName: string, maxSuggestions = 6): NameCheckResult {
    const normalized = StringUtil.normalize(companyName);

    const conflictName = this.getConflictingName(normalized);
    const isTaken = !!conflictName;

    const suggestions = this.generateSuggestions(companyName, normalized, maxSuggestions);

    return {
      available: !isTaken,
      suggestions,
      message: isTaken
        ? `This name is too similar to an existing registered entity: "${conflictName}". Here are some alternatives:`
        : 'This name is available!',
    };
  }


  private getConflictingName(normalizedName: string): string | null {
    for (const taken of this.normalizedTakenNames) {
      const similarity = StringUtil.calculateSimilarity(normalizedName, taken);

      if (similarity >= this.SIMILARITY_BLOCK || 
          (similarity >= this.SIMILARITY_WARN && this.hasSameCoreWords(normalizedName, taken))) {
        return taken;
      }
    }
    return null;
  }

  private extractCoreWords(name: string): string[] {
    return StringUtil.normalize(name)
      .split(' ')
      .filter(word => !this.ignoredWords.includes(word));
  }

  private hasSameCoreWords(name1: string, name2: string): boolean {
    const core1 = this.extractCoreWords(name1);
    const core2 = this.extractCoreWords(name2);
    return core1.some(word => core2.includes(word));
  }

  private generateSuggestions(
    originalName: string,
    normalizedName: string,
    maxSuggestions: number,
  ): NameSuggestion[] {
    let suggestions: NameSuggestion[] = [];

    // 1. Similar existing names
    suggestions.push(...this.findSimilarNames(normalizedName));

    // 2. Variations (prefix + suffix)
    suggestions.push(...this.generateNameVariations(originalName));

    // 3. Generated strategies (industry/geography)
    suggestions.push(...this.generateVariations(originalName));

    // Remove duplicates
    suggestions = this.removeDuplicates(suggestions);

    // Sort by score, push conflicts down
    suggestions.sort((a, b) => {
      const penaltyA = a.category === 'similar' ? 0.2 : 0;
      const penaltyB = b.category === 'similar' ? 0.2 : 0;
      return (b.score - penaltyB) - (a.score - penaltyA);
    });

    return suggestions.slice(0, maxSuggestions);
  }

  private findSimilarNames(normalizedInput: string): NameSuggestion[] {
    if (normalizedInput.length < 2) return [];

    const matches: NameSuggestion[] = [];

    this.takenNames.forEach((name) => {
      const matchResult = StringUtil.calculateMatchScore(normalizedInput, name);

      if (matchResult.score > 0.3 || matchResult.matchType !== 'similar') {
        matches.push({
          name,
          score: matchResult.score,
          matchType: matchResult.matchType,
          category: 'similar',
          reason: 'Already registered',
        });
      }
    });

    return matches.slice(0, 2);
  }

  private generateNameVariations(originalName: string): NameSuggestion[] {
    const baseName = originalName.trim();
    const variations: NameSuggestion[] = [];
    this.suffixes.slice(0, 4).forEach((suffix, index) => {
      variations.push({
        name: `${baseName} ${suffix}`,
        score: 0.9 - index * 0.05,
        matchType: 'similar',
        category: 'variation',
        reason: 'Available variation',
      });
    });

    // Prefix variations
    this.prefixes.slice(0, 2).forEach((prefix, index) => {
      variations.push({
        name: `${prefix} ${baseName}`,
        score: 0.85 - index * 0.05,
        matchType: 'similar',
        category: 'variation',
        reason: 'Available variation',
      });
    });

    return variations;
  }

  private generateVariations(baseName: string): NameSuggestion[] {
    return [
      {
        name: `${baseName} Technologies`,
        score: 0.88,
        matchType: 'similar',
        category: 'generated',
        reason: 'Industry-aligned name',
      },
      {
        name: `${baseName} Africa`,
        score: 0.87,
        matchType: 'similar',
        category: 'generated',
        reason: 'Geographic distinction',
      },
      {
        name: `${baseName} & Co`,
        score: 0.85,
        matchType: 'similar',
        category: 'generated',
        reason: 'Classic business format',
      },
    ];
  }

  private removeDuplicates(suggestions: NameSuggestion[]): NameSuggestion[] {
    const seen = new Set<string>();
    return suggestions.filter((s) => {
      const normalized = StringUtil.normalize(s.name);
      if (seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    });
  }

  getCategoryIcon(category: 'similar' | 'variation' | 'generated'): string {
    const icons = {
      similar: 'info',
      variation: 'auto_awesome',
      generated: 'tips_and_updates',
    };
    return icons[category];
  }

  getCategoryColor(category: 'similar' | 'variation' | 'generated'): string {
    const colors = {
      similar: 'text-orange-600',
      variation: 'text-blue-600',
      generated: 'text-green-600',
    };
    return colors[category];
  }
}
